// frontend/src/store/cohesionStore.ts
// Brighella v2 (ADR-013 cross-cutting #6 density + #3 watchlist + #4 entity
// drawer) — Zustand store for global v2 chrome state.
//
// Persisted per-user via localStorage with a versioned namespace key.
// Migrate the key when the shape changes; never silently mutate persisted
// values that other tabs may be reading.

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type Density = 'compact' | 'cozy' | 'comfortable'

export type WatchlistEntityType =
  | 'production'
  | 'person'
  | 'space'
  | 'event'
  | 'saved-view'

export interface WatchlistItem {
  /** Stable kind of entity (used to render the right icon). */
  type: WatchlistEntityType
  /** Numeric or string id; saved-view ids are saved_views.id from the backend. */
  id: number | string
  /** Display label cached at pin time so we render before fetching detail. */
  label: string
  /** Optional sub-label rendered under the main label. */
  subLabel?: string
}

export type DrawerEntityType =
  | 'production'
  | 'person'
  | 'space'
  | 'event'
  | 'item'
  | 'report'

export interface DrawerTarget {
  type: DrawerEntityType
  id: number | string
}

/**
 * Workspace-persistence key. Encodes "the last tab the user was on inside
 * entity (type, id)" — e.g. lastTab["production:42"] === "budget".
 *
 * Production-as-workspace UX (ADR-013 #2): when a user comes back to an
 * entity they previously visited, they land on the tab they were last on.
 */
export type LastTabMap = Record<string, string>

export interface CohesionState {
  /** Per-user density preference applied app-wide. */
  density: Density
  /** Watchlist chips on the Today surface. Order = display order. */
  watchlist: WatchlistItem[]
  /** Currently-open entity drawer, or null when closed. */
  drawer: DrawerTarget | null
  /** Last visited tab per entity (workspace persistence). */
  lastTab: LastTabMap

  // Density
  setDensity: (d: Density) => void

  // Watchlist
  pinWatchlist: (item: WatchlistItem) => void
  unpinWatchlist: (type: WatchlistEntityType, id: number | string) => void
  reorderWatchlist: (next: WatchlistItem[]) => void
  isWatched: (type: WatchlistEntityType, id: number | string) => boolean

  // Drawer
  openDrawer: (target: DrawerTarget) => void
  closeDrawer: () => void

  // Workspace persistence
  rememberTab: (entityType: string, entityId: number | string, tab: string) => void
  recallTab:   (entityType: string, entityId: number | string) => string | null
}

const DEFAULT_DENSITY: Density = 'cozy'

export const useCohesionStore = create<CohesionState>()(
  persist(
    (set, get) => ({
      density: DEFAULT_DENSITY,
      watchlist: [],
      drawer: null,
      lastTab: {},

      setDensity: (density) => set({ density }),

      pinWatchlist: (item) => {
        const list = get().watchlist
        if (list.some((w) => w.type === item.type && w.id === item.id)) return
        set({ watchlist: [...list, item] })
      },

      unpinWatchlist: (type, id) => {
        set({
          watchlist: get().watchlist.filter(
            (w) => !(w.type === type && w.id === id),
          ),
        })
      },

      reorderWatchlist: (next) => set({ watchlist: next }),

      isWatched: (type, id) =>
        get().watchlist.some((w) => w.type === type && w.id === id),

      openDrawer: (target) => set({ drawer: target }),
      closeDrawer: () => set({ drawer: null }),

      rememberTab: (entityType, entityId, tab) => {
        const key = `${entityType}:${entityId}`
        set({ lastTab: { ...get().lastTab, [key]: tab } })
      },

      recallTab: (entityType, entityId) => {
        const key = `${entityType}:${entityId}`
        return get().lastTab[key] ?? null
      },
    }),
    {
      // Versioned key so we can migrate cleanly when the persisted shape changes.
      name: 'brighella-cohesion-v1',
      // Drawer state is per-session, not per-user — don't persist it.
      partialize: (s) => ({
        density:   s.density,
        watchlist: s.watchlist,
        lastTab:   s.lastTab,
      }),
    },
  ),
)

/** Reset hook for tests. Outside React; safe to call from beforeEach. */
export function _resetCohesionStoreForTests(): void {
  useCohesionStore.setState({
    density: DEFAULT_DENSITY,
    watchlist: [],
    drawer: null,
    lastTab: {},
  })
}
